import { setSkins, transition, type SpineArtboardSpec, type SpinePose } from "@slot/presentation/rive-spine";
import { POTS, type PotKind } from "../../rules/symbols";
import { BOARD_IMPACT_RIVE } from "../BoardImpact";
import { CELL_FIRE_RIVE } from "../CellFires";
import { CELL_HIT_RIVE } from "../CellHits";
import { COIN_IDLE_RIVE } from "../CoinShines";
import { COLLECTOR_RIVE } from "../Collectors";
import { FIRE_STREAK_RIVE } from "../FireStreaks";
import { FLYING_LIGHT_RIVE } from "../FlyingLights";
import { EDGE_FIRE_RIVE } from "../GridEdgeFire";
import { BOARD_IMPACT_LAYOUT, BONUS_TRANSITION_LAYOUT, CELL_FIRE_LAYOUT, COIN_IDLE_LAYOUT, COLLECTOR_LAYOUT, EDGE_FIRE_LAYOUT, FIRE_STREAK_LAYOUT, FLYING_LIGHT_LAYOUT, IMPACT_LAYOUT, POT_ERUPTION_LAYOUT, POT_SHINE_LAYOUT, POT_SMOKE_LAYOUT, POTION_POTS_LAYOUT } from "../layout";
import { IMPACT_RIVE, POT_ERUPTION_RIVE } from "../PotBursts";
import { POT_SMOKE_RIVE } from "../PotSmoke";
import { POTION_POTS_RIVE } from "../PotsView";
import { BONUS_TRANSITION_RIVE } from "../../views/game-transition/BonusTransition";

/*
 * The artist's six Rive files as rive-spine converted them (2026-10-11): each a Spine 4.2 skeleton whose tracks are the
 * file's state machine layers, in order (a later track wins, as a later layer does). A layer whose states only hold
 * opacities is a skin (each file's colour, each pot's level): its skin is shown and its state's animation played on
 * its track. The inputs the views set (the Rive view models' names) are calls on the tracks, made as the state machine's
 * transitions would: what follows a state at its end is queued after it, a mixed transition mixes as long.
 */

/** A file's `color` number (the views': 1 LIFE, 2 MULTI, 3 GROW) and its colour state: a skin and an animation. */
const COLOUR_STATES: Readonly<Record<number, string>> = { 1: "color_life", 2: "color_multi", 3: "color_grow" };

/** The colour layer (track 0) in `value`'s state, at once (an unknown number leaves it, as Rive's has no state for it). */
function setColour(spine: SpinePose, value: number): void {
  const state = COLOUR_STATES[value];
  if (!state) return;
  setSkins(spine.skeleton, [state]);
  spine.state.setAnimation(0, state, false);
}

/** The animation a track plays now (null: none). */
function playing(spine: SpinePose, track: number): string | null {
  return spine.state.getCurrent(track)?.animation?.name ?? null;
}

/**
 * The pots (`potion_pots.riv`, artboard top-left at its origin): per pot a level layer (track 0 LIFE, 6 GROW, 12 MULTI;
 * a skin per level, shown at once) and its five idle loops on the tracks after it, which run from the start whatever the
 * level, so a change never restarts them.
 */
export const POTION_POTS_SPINE: SpineArtboardSpec = {
  artboard: POTION_POTS_LAYOUT.artboard,
  origin: { x: 0, y: 0 },
  controls(spine) {
    const tracks = { life: 0, grow: 6, multi: 12 } satisfies Record<PotKind, number>;
    const idles = ["flow_idle", "surface_idle", "overflow_idle", "sparkle_idle", "particles_idle"] as const;
    const levels: Record<PotKind, number> = { life: 0, multi: 0, grow: 0 };
    const show = (): void => setSkins(spine.skeleton, [`life_level${levels.life}`, `grow_level${levels.grow}`, `multi_level${levels.multi}`]);
    const setLevel = (kind: PotKind, level: number): void => {
      if (!Number.isInteger(level) || level < 0 || level > POTION_POTS_RIVE.maxLevel) return;
      levels[kind] = level;
      show();
      spine.state.setAnimation(tracks[kind], `${kind}_level${level}`, false);
    };
    show();
    for (const kind of POTS) {
      spine.state.setAnimation(tracks[kind], `${kind}_level0`, false);
      idles.forEach((idle, i) => spine.state.setAnimation(tracks[kind] + 1 + i, `${kind}_${idle}`, true));
    }
    const { levels: names } = POTION_POTS_RIVE;
    return { numbers: { [names.life]: (level) => setLevel("life", level), [names.multi]: (level) => setLevel("multi", level), [names.grow]: (level) => setLevel("grow", level) } };
  },
};

/**
 * The shine behind the pots (`pot_shine.riv`, artboard top-left at its origin): each pot's shine on its own layer (tracks
 * 0 LIFE, 1 MULTI, 2 GROW, on from the start, as the file's booleans default), the shimmer — rays that sway,
 * cross-fade and breathe — on a 10 s loop after them. The game keeps every pot's on: it sets no input. Not multisampled
 * (rive-spine measured it the same either way); its texture is the artboard, which cuts its rays where Rive's canvas does.
 */
export const POT_SHINE_SPINE: SpineArtboardSpec = {
  artboard: POT_SHINE_LAYOUT.artboard,
  origin: { x: 0, y: 0 },
  antialias: false,
  controls(spine) {
    ["life_on", "multi_on", "grow_on"].forEach((on, track) => spine.state.setAnimation(track, on, false));
    spine.state.setAnimation(3, "shimmer", true);
    return {};
  },
};

/** The flying light (`flying_light.riv`, its origin the comet's head): the colour, then its flight, sparks and pulse loops. */
export const FLYING_LIGHT_SPINE: SpineArtboardSpec = {
  artboard: FLYING_LIGHT_LAYOUT.artboard,
  origin: FLYING_LIGHT_LAYOUT.head,
  controls(spine) {
    setColour(spine, 3);
    spine.state.setAnimation(1, "flight", true);
    spine.state.setAnimation(2, "sparks", true);
    spine.state.setAnimation(3, "pulse", true);
    return { numbers: { [FLYING_LIGHT_RIVE.color]: (value) => setColour(spine, value) } };
  },
};

/**
 * A burst (`impact.riv`'s `burst`, `pot_eruption.riv`'s `erupt`): the colour, then idle (nothing drawn) until its
 * trigger plays the burst once (again mid-burst, from its start) and idle after it.
 */
function burst(input: { artboard: SpineArtboardSpec["artboard"]; origin: SpineArtboardSpec["origin"]; colour: number; trigger: string; colorInput: string; animation: string }): SpineArtboardSpec {
  return {
    artboard: input.artboard,
    origin: input.origin,
    controls(spine) {
      setColour(spine, input.colour);
      spine.state.setAnimation(1, "idle", false);
      return {
        numbers: { [input.colorInput]: (value) => setColour(spine, value) },
        triggers: {
          [input.trigger]: () => {
            spine.state.setAnimation(1, input.animation, false);
            spine.state.addAnimation(1, "idle", false, 0);
          },
        },
      };
    },
  };
}

/** The impact (`impact.riv`, its origin the hit point on a pot's opening). */
export const IMPACT_SPINE = burst({ artboard: IMPACT_LAYOUT.artboard, origin: IMPACT_LAYOUT.hit, colour: 3, trigger: IMPACT_RIVE.trigger, colorInput: IMPACT_RIVE.color, animation: "burst" });

/**
 * MULTI's hit on a cell (`cell_fire.riv`'s `CellHit`, its origin the artboard's centre: the cell's): one track, idle until
 * `hit` plays the burst once (from its start again mid-burst), then idle. No groups. Not multisampled (rive-spine
 * measured a 2×2 block at most 2.5 levels off at every scale).
 */
export const CELL_HIT_SPINE: SpineArtboardSpec = {
  artboard: CELL_FIRE_LAYOUT.hit,
  origin: { x: CELL_FIRE_LAYOUT.hit.width / 2, y: CELL_FIRE_LAYOUT.hit.height / 2 },
  antialias: false,
  controls(spine) {
    spine.state.setAnimation(0, "idle", false);
    return {
      triggers: {
        [CELL_HIT_RIVE.trigger]: () => {
          spine.state.setAnimation(0, "hit", false);
          spine.state.addAnimation(0, "idle", false, 0);
        },
      },
    };
  },
};

/** Rive's mix between a burning cell's switch states (80 ms), from one turning to the other. */
const CELL_FIRE_MIX_SECONDS = 0.08;

/**
 * A cell burning (`cell_fire.riv`'s `CellFire`, its origin the artboard's centre: the cell's): `Switch` on track 0 (off,
 * igniting, on, going out: `lit` switches it through rive-spine's `transition`, mixed as Rive mixes from the other
 * turning) and `Burn` on track 1, looping from the start; its opacity group is `SpineArtboard`'s. Not multisampled
 * (rive-spine measured a 2×2 block at most 1.8 levels off down to half scale, 4.5 at a quarter, on a few blocks).
 */
export const CELL_FIRE_SPINE: SpineArtboardSpec = {
  artboard: CELL_FIRE_LAYOUT.fire,
  origin: { x: CELL_FIRE_LAYOUT.fire.width / 2, y: CELL_FIRE_LAYOUT.fire.height / 2 },
  antialias: false,
  controls(spine) {
    spine.state.setAnimation(0, "fire_off", false);
    spine.state.setAnimation(1, "burn_loop", true);
    return {
      booleans: {
        [CELL_FIRE_RIVE.lit]: (lit) => {
          const name = playing(spine, 0);
          if (lit) transition(spine.state, 0, "fire_ignite", name === "fire_out" ? CELL_FIRE_MIX_SECONDS : 0, { then: { animation: "fire_on" } });
          else transition(spine.state, 0, "fire_out", name === "fire_ignite" ? CELL_FIRE_MIX_SECONDS : 0, { then: { animation: "fire_off" } });
        },
      },
    };
  },
};

/** The idle coin's variants: the loop each plays (`idle_v0` to `idle_v3`, the same loop a quarter later each). */
const COIN_IDLE_VARIANTS = 4;

/**
 * The plain coin's idle shine (`coin_idle.riv`'s `Coin`, its origin the artboard's centre: the coin's): one track, a
 * variant's loop playing from its start, its number `variant` picking which (the file's four loops, each the same 20 s
 * loop with its keys a quarter later). No groups.
 */
export const COIN_IDLE_SPINE: SpineArtboardSpec = {
  artboard: COIN_IDLE_LAYOUT.artboard,
  origin: { x: COIN_IDLE_LAYOUT.artboard.width / 2, y: COIN_IDLE_LAYOUT.artboard.height / 2 },
  controls(spine) {
    spine.state.setAnimation(0, "idle_v0", true);
    return {
      numbers: {
        [COIN_IDLE_RIVE.variant]: (variant) => {
          const which = Math.min(COIN_IDLE_VARIANTS - 1, Math.max(0, Math.round(variant)));
          spine.state.setAnimation(0, `idle_v${which}`, true);
        },
      },
    };
  },
};

/**
 * The collect coin's collector (`collector.riv`'s `Collector`, its origin the artboard's centre: the cell's): `Idle` on
 * track 0, the 20 s swirl looping from the start; `FX` on track 1, at rest until `collect` (a 0.4 s pulse) or `activate`
 * (a 1 s flare) plays once, cut in at once as Rive does (again from its start when fired mid-play), then at rest; the
 * two animate bones and slots of their own. No groups. Not multisampled (rive-spine measured a 2×2 block at most 2.3
 * levels off at half scale, less below it, where the board draws it).
 */
export const COLLECTOR_SPINE: SpineArtboardSpec = {
  artboard: COLLECTOR_LAYOUT.artboard,
  origin: { x: COLLECTOR_LAYOUT.artboard.width / 2, y: COLLECTOR_LAYOUT.artboard.height / 2 },
  antialias: false,
  controls(spine) {
    spine.state.setAnimation(0, "idle_loop", true);
    spine.state.setAnimation(1, "rest", false);
    const play = (animation: string) => (): void => {
      spine.state.setAnimation(1, animation, false);
      spine.state.addAnimation(1, "rest", false, 0);
    };
    return { triggers: { [COLLECTOR_RIVE.collect]: play("collect"), [COLLECTOR_RIVE.activate]: play("activate") } };
  },
};

/**
 * GROW's board impact (`board_impact.riv`, its origin the hit point, the artboard's centre): one track, idle until `hit`
 * plays the burst once (from its start again mid-burst, as Rive switches to its second `hit` state), then idle. No
 * mixes, skins or groups. Multisampled: single-sampled, the burst's first 0.2 s has hard edges (rive-spine measured up
 * to 14/255; 3.5 multisampled).
 */
export const BOARD_IMPACT_SPINE: SpineArtboardSpec = {
  artboard: BOARD_IMPACT_LAYOUT.artboard,
  origin: BOARD_IMPACT_LAYOUT.hit,
  controls(spine) {
    spine.state.setAnimation(0, "idle", false);
    return {
      triggers: {
        [BOARD_IMPACT_RIVE.trigger]: () => {
          spine.state.setAnimation(0, "hit", false);
          spine.state.addAnimation(0, "idle", false, 0);
        },
      },
    };
  },
};

/**
 * A pot's eruption (`pot_eruption.riv`, its origin the base on a pot's opening), over its artboard grown to all it draws
 * (`POT_ERUPTION_LAYOUT`: its jets fly on). Not multisampled: it has no screen-blended layer and its edges are soft
 * (rive-spine measured a 2×2 block at most 1 level off at full scale, 2.8 at half), and its texture is tall.
 */
export const POT_ERUPTION_SPINE: SpineArtboardSpec = {
  ...burst({ artboard: POT_ERUPTION_LAYOUT.artboard, origin: POT_ERUPTION_LAYOUT.base, colour: 2, trigger: POT_ERUPTION_RIVE.trigger, colorInput: POT_ERUPTION_RIVE.color, animation: "erupt" }),
  antialias: false,
};

/**
 * A fire streak (`fire_streak.riv`, its origin the fireball's head): the colour, flying from the start, its flames on a
 * loop; `land` (flying or relighting) collapses it into its ring of fire, then nothing; `launch` (landing or landed)
 * relights it, then it flies.
 */
export const FIRE_STREAK_SPINE: SpineArtboardSpec = {
  artboard: FIRE_STREAK_LAYOUT.artboard,
  origin: FIRE_STREAK_LAYOUT.head,
  controls(spine) {
    setColour(spine, 2);
    spine.state.setAnimation(1, "flying", false);
    spine.state.setAnimation(2, "flame_loop", true);
    const go = (from: readonly string[], animation: string, then: string): void => {
      if (!from.includes(playing(spine, 1) ?? "")) return;
      spine.state.setAnimation(1, animation, false);
      spine.state.addAnimation(1, then, false, 0);
    };
    return {
      numbers: { [FIRE_STREAK_RIVE.color]: (value) => setColour(spine, value) },
      triggers: { [FIRE_STREAK_RIVE.land]: () => go(["flying", "ignite"], "land", "done"), [FIRE_STREAK_RIVE.launch]: () => go(["land", "done"], "ignite", "flying") },
    };
  },
};

/** How long the edge fire's dying out mixes into its igniting when it is lit again mid-way (Rive's out → ignite). */
const EDGE_FIRE_RELIGHT_MIX_SECONDS = 0.1;

/**
 * A piece of GROW's edge fire (`edge_fire.riv`'s `EdgeFireTop` or `EdgeFireBottom`, its origin the frame's outer corner
 * it rides): off (nothing drawn) with its flames looping under it from the start; `on` ignites it (0.75 s, its clip
 * racing to the corners) and holds it burning, off dies it out (0.4 s) to off — lit again as it dies out, it mixes back
 * into igniting for a tenth of a second. The switch track is `transition`'s, which switches as Rive's state machine
 * does: on the frame, a change during the mix waiting for its end.
 */
function edgeFire(origin: SpineArtboardSpec["origin"]): SpineArtboardSpec {
  return {
    artboard: EDGE_FIRE_LAYOUT.artboard,
    origin,
    controls(spine) {
      spine.state.setAnimation(0, "fire_off", false);
      spine.state.setAnimation(1, "flame_loop", true);
      const setOn = (on: boolean): void => {
        const name = playing(spine, 0);
        if (on) {
          if (name !== "fire_on") transition(spine.state, 0, "fire_ignite", name === "fire_out" ? EDGE_FIRE_RELIGHT_MIX_SECONDS : 0, { then: { animation: "fire_on" } });
        } else if (name !== "fire_off") {
          transition(spine.state, 0, "fire_out", 0, { then: { animation: "fire_off" } });
        }
      };
      return { booleans: { [EDGE_FIRE_RIVE.on]: setOn } };
    },
  };
}

/** The edge fire's top piece, its origin the frame's outer top-left corner. */
export const EDGE_FIRE_TOP_SPINE = edgeFire(EDGE_FIRE_LAYOUT.top);

/** The edge fire's bottom piece, its origin the frame's outer bottom-left corner. */
export const EDGE_FIRE_BOTTOM_SPINE = edgeFire(EDGE_FIRE_LAYOUT.bottom);

/** How long a ramp of the smoke mixes into the other when it turns mid-ramp (Rive's intro ↔ outro transitions). */
const SMOKE_TURN_MIX_SECONDS = 0.1;

/**
 * A pot's smoke (`pot_smoke.riv`, its origin the base on a pot's opening): the colour, off (nothing drawn) with its plume
 * looping under it from the start; `triggered` on ramps it up (0.4 s) and holds it on, off ramps it down (1/3 s) to off
 * — a ramp turned before its end mixing into the other for a tenth of a second.
 */
export const POT_SMOKE_SPINE: SpineArtboardSpec = {
  artboard: POT_SMOKE_LAYOUT.artboard,
  origin: POT_SMOKE_LAYOUT.base,
  controls(spine) {
    setColour(spine, 3);
    spine.state.setAnimation(1, "smoke_off", false);
    spine.state.setAnimation(2, "plume_loop", true);
    const setTriggered = (on: boolean): void => {
      const current = spine.state.getCurrent(1);
      const name = current?.animation?.name ?? null;
      // Already there or on its way: nothing changes.
      if (on ? name === "smoke_intro" || name === "smoke_on" : name === "smoke_outro" || name === "smoke_off") return;
      const turning = current !== null && !current.isComplete() && (name === "smoke_intro" || name === "smoke_outro");
      const entry = spine.state.setAnimation(1, on ? "smoke_intro" : "smoke_outro", false);
      entry.mixDuration = turning ? SMOKE_TURN_MIX_SECONDS : 0;
      spine.state.addAnimation(1, on ? "smoke_on" : "smoke_off", false, 0);
    };
    return { numbers: { [POT_SMOKE_RIVE.color]: (value) => setColour(spine, value) }, booleans: { [POT_SMOKE_RIVE.triggered]: setTriggered } };
  },
};

/**
 * The way into the bonus (`bonus_transition.riv`, its origin the artboard's centre): empty (`idle`, nothing drawn) until
 * `play` plays the potion explosion once (2 s), from its start again if played mid-way, then empty again. No mixes,
 * skins or clips; its screen dimming is a 4 px solid-colour image the converter stretches to Rive's rectangle.
 */
export const BONUS_TRANSITION_SPINE: SpineArtboardSpec = {
  artboard: BONUS_TRANSITION_LAYOUT.artboard,
  origin: { x: BONUS_TRANSITION_LAYOUT.artboard.width / 2, y: BONUS_TRANSITION_LAYOUT.artboard.height / 2 },
  controls(spine) {
    spine.state.setAnimation(0, "idle", false);
    return {
      triggers: {
        [BONUS_TRANSITION_RIVE.play]: () => {
          spine.state.setAnimation(0, "play", false);
          spine.state.addAnimation(0, "idle", false, 0);
        },
      },
    };
  },
};
